import type { RouteRecordRaw } from 'vue-router'
import { checkoutRoutes } from './checkout/routes'

/**
 * The public marketplace (Phase 08).
 *
 * Every route is `public: true` and carries the **marketplace** layout, which is what selects
 * the marketplace Naive theme — the two design languages can never appear on one screen
 * (CLAUDE.md rule 6). No route here requires authentication; the nav's "Sign in" is an
 * affordance, not a gate.
 *
 * Each page is its own lazily-imported chunk, and none of them imports anything from
 * `modules/landlord`, `modules/affiliate` or `modules/tenancy` — asserted by the bundle check.
 */
/**
 * The public marketplace's routes are **not** gated on `v2_marketplace` here, and that is a
 * decision rather than an omission (frontend phase-14 §2.1).
 *
 * A route flag is read from `auth.featureFlags`, which arrives in the session bootstrap — and
 * an anonymous visitor has no session. Resolving flags for them would mean a blocking request
 * to `/api/v2/meta/` before the first route renders, on the one path whose LCP budget is
 * already missed (`docs/v2/performance-report.md`).
 *
 * The control for the public site is the one stage 4 actually describes: **V2's marketplace
 * lives at a new path alongside V1's site**, so turning it off means not routing traffic to it
 * and not linking it. The API is flagged as well, so a visitor who finds the path with the flag
 * off gets empty results rather than data. Nothing here exposes a listing the server would not.
 */
const marketplace = { layout: 'marketplace' as const, public: true }

/** `/locations/Anambra/Awka` as the listings filter `?state=Anambra&city=Awka`. */
function locationQuery(params: Record<string, string | string[]>): Record<string, string> {
  const query: Record<string, string> = {}
  for (const key of ['state', 'city'] as const) {
    const value = params[key]
    if (typeof value === 'string' && value) query[key] = value
  }
  return query
}

export const marketplaceRoutes: RouteRecordRaw[] = [
  {
    path: '/become-an-affiliate',
    name: 'marketplace-affiliate-application',
    component: () => import('./pages/AffiliateApplicationPage.vue'),
    meta: { layout: 'auth', public: true },
  },
  // Phase 09. Declared first so `/checkout/:id/details` cannot be swallowed by a broader
  // marketplace pattern, and so the journey reads in order in this file.
  ...checkoutRoutes,
  {
    path: '/',
    name: 'home',
    component: () => import('./pages/MarketplaceHomePage.vue'),
    meta: marketplace,
  },
  {
    path: '/listings',
    name: 'marketplace-listings',
    component: () => import('./pages/ListingsPage.vue'),
    meta: marketplace,
  },
  {
    path: '/listings/:slug',
    name: 'marketplace-listing',
    component: () => import('./pages/ListingDetailPage.vue'),
    meta: marketplace,
  },
  {
    path: '/properties',
    name: 'marketplace-properties',
    component: () => import('./pages/PropertiesPage.vue'),
    meta: marketplace,
  },
  {
    path: '/properties/:slug',
    name: 'marketplace-property',
    component: () => import('./pages/PropertyDetailPage.vue'),
    meta: marketplace,
  },
  // The location pages became the listings page's location filter (2026-10-28). Their addresses
  // -- in links, bookmarks and search results -- keep working as that search, already filtered.
  // The marketplace meta matches the boot screen, which paints these paths as the marketplace.
  // `/listings` is written out: importing `ROUTES` would pull constants.ts into the entry chunk.
  {
    path: '/locations/:state?/:city?',
    redirect: (to) => ({ path: '/listings', query: locationQuery(to.params) }),
    meta: marketplace,
  },
  // "Stop these emails" from a follow-up (2026-10-10). Before `/request/:campus`, so "stop" is
  // never read as a campus.
  {
    path: '/request/stop',
    name: 'marketplace-space-request-stop',
    component: () => import('./pages/SpaceRequestStopPage.vue'),
    meta: marketplace,
  },
  // A requester follows their request and writes to APMP (2026-10-12), from the signed link in
  // every email about it. Before `/request/:campus`, so "track" is never read as a campus.
  {
    path: '/request/track',
    name: 'marketplace-space-request-track',
    component: () => import('./pages/SpaceRequestTrackPage.vue'),
    meta: marketplace,
  },
  // Students' way in from the home page and the footer (2026-10-22): a room or a roommate,
  // per school.
  {
    path: '/students',
    name: 'marketplace-students',
    component: () => import('./pages/StudentsPage.vue'),
    meta: marketplace,
  },
  // A student looking for a roommate (2026-10-22): the proposal behind the signed link in its
  // email, and "Stop looking". Before `/request/:campus`, so "roommate" is never read as a
  // campus.
  {
    path: '/request/roommate',
    name: 'marketplace-roommate-proposal',
    component: () => import('./pages/roommates').then((m) => m.RoommateProposalPage),
    meta: marketplace,
  },
  {
    path: '/request/roommate/stop',
    name: 'marketplace-roommate-stop',
    component: () => import('./pages/roommates').then((m) => m.RoommateStopPage),
    meta: marketplace,
  },
  // The student follows their roommate request and writes to APMP (2026-10-26), from the signed
  // link in the emails about it -- officers handle roommate requests like space requests.
  {
    path: '/request/roommate/track',
    name: 'marketplace-roommate-track',
    component: () => import('./pages/roommates').then((m) => m.RoommateTrackPage),
    meta: marketplace,
  },
  // A campus's space-request form -- the link a university puts in its admission email
  // (2026-10-09). `/request/unizik` is the one issued; an unknown campus says so on the page.
  {
    path: '/request/:campus',
    name: 'marketplace-space-request',
    component: () => import('./pages/SpaceRequestPage.vue'),
    meta: marketplace,
  },
  {
    path: '/list-your-property',
    name: 'marketplace-list-your-property',
    component: () => import('./pages/ListYourPropertyPage.vue'),
    meta: marketplace,
  },
  // The page set a rental marketplace is expected to have (marketplace-rebuild.md, "The page
  // set"). Static pages describe only what the platform actually does; Saved resolves the
  // browser's saved slugs live; Contact posts a general enquiry through the same endpoint the
  // listing form uses.
  {
    path: '/how-it-works',
    name: 'marketplace-how-it-works',
    component: () => import('./pages/HowItWorksPage.vue'),
    meta: marketplace,
  },
  {
    path: '/about',
    name: 'marketplace-about',
    component: () => import('./pages/AboutPage.vue'),
    meta: marketplace,
  },
  {
    path: '/saved',
    name: 'marketplace-saved',
    component: () => import('./pages/SavedPage.vue'),
    meta: marketplace,
  },
  {
    path: '/help',
    name: 'marketplace-help',
    component: () => import('./pages/HelpPage.vue'),
    meta: marketplace,
  },
  {
    path: '/privacy',
    name: 'marketplace-privacy',
    component: () => import('./pages/PrivacyPage.vue'),
    meta: marketplace,
  },
  {
    path: '/terms',
    name: 'marketplace-terms',
    component: () => import('./pages/TermsPage.vue'),
    meta: marketplace,
  },
]
